/**
 * A doc's body drawn as its blocks (slice 2g spec §8; Paper boards 52–55):
 * `parseBlocks`, then each `text` block with `DocMarkdown` and each other
 * block with its own piece, in Crew's style. A broken block shows its source
 * under the reason it couldn't be drawn. Photos only ever come from Crew's
 * copy (`GET /api/files/:id`), never another host.
 *
 * It's laid out by the width it has (a container query at 34rem): the
 * letter-width sheet at 1440 and the editor's Preview get the wide layout,
 * a phone's page the narrow one, whatever the window.
 */
import { Dialog, DialogContent, DialogTitle } from "@crew/ui/components/dialog";
import { Timeline } from "@crew/ui/components/timeline";
import { cn } from "@crew/ui/lib/utils";
import { ImageOff, Info, Lightbulb, TriangleAlert } from "lucide-react";
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  parseBlocks,
  type Block,
  type CalloutBlock,
  type ChartBlock,
  type ColumnsBlock,
  type DetailsBlock,
  type FactsBlock,
  type FigureBlock,
  type Pair,
  type ProductBlock,
  type ProductsBlock,
  type RowsBlock,
  type SectionBlock,
  type StepsBlock,
  type TextBlock,
} from "../../../worker/domain/blocks.js";
import { fileUrl } from "../messaging/attached";
import { bars, formatValue, LINE, LINE_PHONE_POINTS, linePoints } from "./chart";
import { DocMarkdown } from "./doc-markdown";

/** A `file:<uuid>` id's address on Crew's own origin. */
const photoUrl = (src: string, size: "thumb" | "full") => fileUrl(src.replace(/^file:/, ""), size);

// ── Photos (spec §7) ────────────────────────────────────────────────────────

/**
 * A photo from Crew's copy: a grey tile holds its space while it loads, and
 * "Photo no longer available" shows when it can't (2f §10.5).
 */
function Photo({
  src,
  alt,
  size,
  className,
  imgClassName,
}: {
  src: string;
  alt: string;
  size: "thumb" | "full";
  className?: string;
  imgClassName?: string;
}) {
  const [state, setState] = useState<"loading" | "shown" | "gone">("loading");
  if (state === "gone") {
    return (
      <span
        data-photo={src}
        data-state="gone"
        className={cn("flex items-center justify-center bg-secondary", className)}
      >
        <span className="flex flex-col items-center gap-1 px-2 text-center text-xs text-muted-foreground">
          <ImageOff aria-hidden className="size-4" />
          Photo no longer available
        </span>
      </span>
    );
  }
  return (
    <span
      data-photo={src}
      data-state={state}
      className={cn("relative block bg-secondary", className)}
    >
      <img
        src={photoUrl(src, size)}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setState("shown")}
        onError={() => setState("gone")}
        className={cn("block size-full", imgClassName, state === "loading" && "opacity-0")}
      />
    </span>
  );
}

function Figure({ block }: { block: FigureBlock }) {
  const [open, setOpen] = useState(false);
  return (
    <figure data-block="figure" className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Open ${block.alt}`}
        className="block cursor-zoom-in overflow-clip rounded-tile focus-visible:outline-2 focus-visible:outline-ring"
      >
        <Photo
          src={block.src}
          alt={block.alt}
          size="full"
          className="min-h-40 w-full"
          imgClassName="h-auto max-h-[360px] object-contain @min-[34rem]:max-h-[480px]"
        />
      </button>
      {block.caption ? (
        <figcaption className="text-meta leading-[18px] text-hint">{block.caption}</figcaption>
      ) : null}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-auto max-w-[calc(100vw-2rem)] gap-2 p-2 sm:max-w-[min(90vw,1100px)]">
          <DialogTitle className="sr-only">{block.alt}</DialogTitle>
          {open ? (
            <img
              src={photoUrl(block.src, "full")}
              alt={block.alt}
              className="h-auto max-h-[85vh] w-auto max-w-[calc(100vw-3rem)] rounded-lg object-contain sm:max-w-[min(88vw,1084px)]"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </figure>
  );
}

// ── Prose ───────────────────────────────────────────────────────────────────

/** Prose, as today; a `#` heading at `##`'s size (the title is the page's one first-level heading). */
function Text({ markdown, className }: { markdown: string; className?: string }) {
  return <DocMarkdown className={cn("[&_h1]:text-2xl", className)}>{markdown}</DocMarkdown>;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-display text-[26px] leading-[30px] font-normal tracking-tight text-foreground">
      {children}
    </h2>
  );
}

const TONES = {
  note: { icon: Info, panel: "bg-secondary", glyph: "text-foreground", title: "text-foreground" },
  tip: { icon: Lightbulb, panel: "bg-moss-tint", glyph: "text-moss", title: "text-moss-ink" },
  warning: {
    icon: TriangleAlert,
    panel: "bg-amber-tint",
    glyph: "text-amber-line",
    title: "text-amber-ink",
  },
} as const;

function Callout({ block }: { block: CalloutBlock }) {
  const tone = TONES[block.tone];
  const Icon = tone.icon;
  return (
    <aside
      data-block="callout"
      data-tone={block.tone}
      className={cn("flex gap-3 rounded-tile px-4 py-3.5", tone.panel)}
    >
      <Icon
        aria-hidden
        strokeWidth={1.8}
        className={cn("mt-0.5 size-[18px] shrink-0", tone.glyph)}
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className={cn("text-[15px] leading-[22px] font-medium", tone.title)}>{block.title}</p>
        {block.markdown ? <Text markdown={block.markdown} /> : null}
      </div>
    </aside>
  );
}

function Columns({ block }: { block: ColumnsBlock }) {
  return (
    <div
      data-block="columns"
      className="flex flex-col gap-2.5 @min-[34rem]:flex-row @min-[34rem]:gap-3"
    >
      {block.columns.map((c, i) => (
        <section
          key={i}
          className="flex min-w-0 flex-1 basis-0 flex-col gap-2 rounded-tile bg-sheet-tint px-4 py-3.5 shadow-[0_0_0_1px] shadow-ink/6"
        >
          <h3 className="text-[14px] leading-5 font-semibold text-foreground">{c.title}</h3>
          <Blocks
            blocks={c.blocks}
            className="gap-3"
            textClassName="space-y-2 text-[14px] leading-[22px]"
          />
        </section>
      ))}
    </div>
  );
}

function Steps({ block }: { block: StepsBlock }) {
  const last = block.items.length - 1;
  return (
    <div data-block="steps">
      <Timeline>
        {block.items.map((s, i) => (
          <li key={i} className="flex gap-3 @min-[34rem]:gap-3.5">
            {s.when ? (
              <span className="hidden w-24 shrink-0 text-meta leading-[22px] text-hint @min-[34rem]:block">
                {s.when}
              </span>
            ) : (
              <span aria-hidden className="hidden w-24 shrink-0 @min-[34rem]:block" />
            )}
            <span aria-hidden className="flex w-3 shrink-0 flex-col items-center">
              <span
                className={cn(
                  "size-[9px] shrink-0 rounded-full bg-foreground @min-[34rem]:mt-[7px]",
                  s.when ? "mt-[22px]" : "mt-[7px]",
                )}
              />
              {i < last ? <span className="mt-0.5 w-px flex-1 bg-ink/15" /> : null}
            </span>
            <div className={cn("flex min-w-0 flex-1 flex-col gap-0.5", i < last && "pb-[18px]")}>
              {s.when ? (
                <span className="text-xs leading-4 text-hint @min-[34rem]:hidden">{s.when}</span>
              ) : null}
              <p className="text-[15px] leading-[22px] font-medium text-foreground">{s.title}</p>
              {s.body ? (
                <p className="text-[14px] leading-[21px] text-muted-foreground">{s.body}</p>
              ) : null}
            </div>
          </li>
        ))}
      </Timeline>
    </div>
  );
}

function Closing({ text }: { text: string }) {
  return (
    <p
      data-block="closing"
      className="border-t border-border pt-3.5 text-meta leading-[19px] text-hint"
    >
      {text}
    </p>
  );
}

// ── Numbers ─────────────────────────────────────────────────────────────────

function Facts({ block }: { block: FactsBlock }) {
  const n = block.items.length;
  return (
    <div
      data-block="facts"
      className={cn(
        "grid grid-cols-2 gap-2.5 @min-[34rem]:gap-3",
        n === 2 || n === 4 ? "@min-[34rem]:grid-cols-2" : "@min-[34rem]:grid-cols-3",
      )}
    >
      {block.items.map((f, i) => (
        <div
          key={i}
          className="flex min-w-0 flex-col gap-1 rounded-tile px-4 py-3.5 shadow-[0_0_0_1px] shadow-ink/10 [&:last-child:nth-child(odd)]:col-span-2 @min-[34rem]:[&:last-child:nth-child(odd)]:col-span-1"
        >
          <span className="text-meta leading-[18px] text-hint">{f.label}</span>
          <span className="font-display text-[28px] leading-8 text-foreground tabular-nums">
            {f.value}
          </span>
          {f.note ? (
            <span className="text-meta leading-[18px] text-muted-foreground">{f.note}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Label and value pairs; a last row whose label starts with "Total" is the total. */
function PairList({ items, framed }: { items: Pair[]; framed: boolean }) {
  const last = items.length - 1;
  return (
    <dl
      className={cn(
        "flex flex-col",
        framed
          ? "overflow-clip rounded-md shadow-[0_0_0_1px] shadow-ink/10"
          : "border-t border-border",
      )}
    >
      {items.map((p, i) => {
        const total = framed && i === last && /^total/i.test(p.label);
        return (
          <div
            key={i}
            data-total={total || undefined}
            className={cn(
              "flex flex-wrap justify-between gap-x-4 text-[14px] leading-5",
              framed ? "px-3.5 py-[9px]" : "py-[7px]",
              (i < last || !framed) && "border-b border-border",
              total && "bg-sheet-tint py-2.5 font-semibold",
            )}
          >
            <dt className={total ? "text-foreground" : "text-hint"}>{p.label}</dt>
            <dd className="ml-auto text-right text-foreground tabular-nums">{p.value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

function Details({ block }: { block: DetailsBlock }) {
  return (
    <div data-block="details">
      <PairList items={block.items} framed />
    </div>
  );
}

function Rows({ block }: { block: RowsBlock }) {
  const numbered = block.items.some((r) => r.number !== undefined);
  const pictured = block.items.some((r) => r.thumbnail);
  return (
    <ul data-block="rows" className="flex flex-col">
      {block.items.map((r, i) => (
        <li
          key={i}
          className="flex items-center gap-2.5 border-b border-border py-2.5 last:border-0 @min-[34rem]:gap-3"
        >
          {numbered ? (
            <span className="w-4 shrink-0 text-[14px] leading-5 text-hint tabular-nums @min-[34rem]:w-6">
              {r.number}
            </span>
          ) : null}
          {pictured ? (
            r.thumbnail ? (
              <Photo
                src={r.thumbnail.src}
                alt={r.thumbnail.alt}
                size="thumb"
                className="size-10 shrink-0 overflow-clip rounded-md @min-[34rem]:size-12"
                imgClassName="object-cover"
              />
            ) : (
              <span aria-hidden className="size-10 shrink-0 @min-[34rem]:size-12" />
            )
          ) : null}
          <span className="flex min-w-0 flex-1 flex-col gap-px">
            {r.url ? (
              <a
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-fit text-[15px] leading-[22px] font-medium text-foreground underline decoration-1 underline-offset-2"
              >
                {r.name} ↗
              </a>
            ) : (
              <span className="text-[15px] leading-[22px] font-medium text-foreground">
                {r.name}
              </span>
            )}
            {r.detail ? (
              <span className="text-meta leading-[18px] text-hint">{r.detail}</span>
            ) : null}
          </span>
          {r.value ? (
            <span className="shrink-0 text-right text-[15px] leading-[22px] text-foreground tabular-nums">
              {r.value}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** The width of the element `ref` is on, as it's laid out; `fallback` before then. */
function useWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const seen = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    seen.observe(el);
    return () => seen.disconnect();
  }, []);
  return { ref, width };
}

/** The sheet's text width at 1440: a line chart's spacing on a phone past 6 points. */
const SHEET_WIDTH = 624;

function BarChart({ block }: { block: ChartBlock }) {
  const scale = bars(block.data.map((d) => d.value));
  const negative = scale.bars.some((b) => b.negative);
  return (
    <div aria-hidden className="flex flex-col gap-2.5">
      {block.data.map((d, i) => {
        const bar = scale.bars[i];
        const value = (
          <span className="shrink-0 text-meta leading-[18px] text-muted-foreground tabular-nums">
            {formatValue(d.value, block.unit)}
          </span>
        );
        return (
          <div
            key={i}
            data-bar={bar.length > 0 ? (bar.negative ? "negative" : "positive") : "none"}
            className="flex items-center gap-3"
          >
            <span className="w-[90px] shrink-0 text-[14px] leading-5 text-copy @min-[34rem]:w-[120px]">
              {d.label}
            </span>
            {negative ? (
              <span className="flex min-w-0 flex-1 items-center gap-2.5">
                <span className="relative h-3.5 flex-1">
                  <span
                    className="absolute inset-y-[-3px] w-px bg-ink/25"
                    style={{ left: `${scale.zero * 100}%` }}
                  />
                  <span
                    className={cn(
                      "absolute inset-y-0 rounded-[3px]",
                      bar.negative ? "bg-brick" : "bg-foreground",
                    )}
                    style={{ left: `${bar.start * 100}%`, width: `${bar.length * 100}%` }}
                  />
                </span>
                <span className="w-[88px] shrink-0 text-right">{value}</span>
              </span>
            ) : (
              <span className="flex min-w-0 flex-1 items-center gap-2.5">
                {bar.length > 0 ? (
                  <span
                    className="h-3.5 rounded-[3px] bg-foreground"
                    style={{ width: `calc((100% - 96px) * ${bar.length})` }}
                  />
                ) : null}
                {value}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LineChart({ block }: { block: ChartBlock }) {
  const { ref, width } = useWidth(SHEET_WIDTH);
  const n = block.data.length;
  const scrolls = n > LINE_PHONE_POINTS && width < SHEET_WIDTH;
  const w = scrolls ? SHEET_WIDTH : Math.max(width, 200);
  const { points, zero } = linePoints(
    block.data.map((d) => d.value),
    w,
  );
  const labelY = LINE.height + 18;
  return (
    <div ref={ref} aria-hidden className="relative">
      <div data-scrolls={scrolls || undefined} className={cn(scrolls && "overflow-x-auto")}>
        <svg
          width={w}
          height={labelY + 4}
          viewBox={`0 0 ${w} ${labelY + 4}`}
          className="block text-foreground"
        >
          <line x1={0} y1={LINE.axis} x2={w} y2={LINE.axis} className="stroke-ink/15" />
          {zero !== null ? (
            <line
              x1={0}
              y1={zero}
              x2={w}
              y2={zero}
              strokeDasharray="3 3"
              className="stroke-ink/25"
            />
          ) : null}
          <polyline
            points={points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinejoin="round"
          />
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={4} fill="currentColor" />
              <text
                x={p.x}
                y={p.y - 14}
                textAnchor="middle"
                className="fill-muted-foreground text-xs tabular-nums"
              >
                {formatValue(block.data[i].value, block.unit)}
              </text>
              <text x={p.x} y={labelY} textAnchor="middle" className="fill-hint text-xs">
                {block.data[i].label}
              </text>
            </g>
          ))}
        </svg>
      </div>
      {scrolls ? (
        <span className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-linear-to-r from-transparent to-background" />
      ) : null}
    </div>
  );
}

function Chart({ block }: { block: ChartBlock }) {
  return (
    <figure data-block="chart" data-kind={block.kind} className="flex flex-col gap-2.5">
      <figcaption className="text-[15px] leading-[22px] font-medium text-foreground">
        {block.title}
      </figcaption>
      {block.kind === "bar" ? <BarChart block={block} /> : <LineChart block={block} />}
      <table className="sr-only">
        <caption>{block.title}</caption>
        <tbody>
          {block.data.map((d, i) => (
            <tr key={i}>
              <th scope="row">{d.label}</th>
              <td>{formatValue(d.value, block.unit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

// ── Shopping ────────────────────────────────────────────────────────────────

const OUT =
  "text-[14px] leading-5 font-medium text-foreground underline decoration-1 underline-offset-2";

function Product({ block }: { block: ProductBlock }) {
  const name = `${block.number !== undefined ? `${block.number}. ` : ""}${block.name}`;
  const nameLine = <p className="text-[17px] leading-6 font-semibold text-foreground">{name}</p>;
  const price = (
    <p className="font-display text-[26px] leading-[30px] text-moss tabular-nums">{block.price}</p>
  );
  const rest = (
    <>
      <p className="text-[15px] leading-[23px] text-copy">{block.note}</p>
      {block.details?.length ? <PairList items={block.details} framed={false} /> : null}
      {block.merchant ? (
        <a
          href={block.merchant.url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(OUT, "w-fit pt-0.5")}
        >
          Buy at {block.merchant.name} ↗
        </a>
      ) : null}
    </>
  );
  if (!block.photo) {
    return (
      <article
        data-block="product"
        className="flex flex-col gap-2 rounded-panel bg-card px-4 py-3.5 shadow-[0_0_0_1px] shadow-ink/10 @min-[34rem]:px-5 @min-[34rem]:py-4"
      >
        <div className="flex items-baseline justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-0.5">
            {nameLine}
            {block.brand ? (
              <p className="text-meta leading-[18px] text-hint">{block.brand}</p>
            ) : null}
          </div>
          <div className="shrink-0">{price}</div>
        </div>
        {rest}
      </article>
    );
  }
  return (
    <article
      data-block="product"
      className="flex flex-col gap-3.5 rounded-panel bg-card p-3 shadow-[0_0_0_1px] shadow-ink/10 @min-[34rem]:flex-row @min-[34rem]:gap-5 @min-[34rem]:p-4"
    >
      <Photo
        src={block.photo}
        alt={block.name}
        size="thumb"
        className="h-[200px] w-full shrink-0 overflow-clip rounded-tile @min-[34rem]:size-[200px]"
        imgClassName="object-cover"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2 px-1 pb-1 @min-[34rem]:p-0">
        <div className="flex flex-col gap-0.5">
          {nameLine}
          {block.brand ? <p className="text-meta leading-[18px] text-hint">{block.brand}</p> : null}
        </div>
        {price}
        {rest}
      </div>
    </article>
  );
}

function Products({ block }: { block: ProductsBlock }) {
  const pictured = block.items.some((p) => p.photo);
  const rows = block.rows ?? [];
  return (
    <div data-block="products" className="flex flex-col gap-3 @min-[34rem]:gap-3.5">
      <div className="-mr-4 flex snap-x snap-mandatory gap-2.5 overflow-x-auto @min-[34rem]:mr-0 @min-[34rem]:snap-none @min-[34rem]:gap-3 @min-[34rem]:overflow-visible">
        {block.items.map((p, i) => (
          <article
            key={i}
            className="flex w-[75%] shrink-0 snap-start flex-col gap-1.5 rounded-panel bg-card p-3 shadow-[0_0_0_1px] shadow-ink/10 @min-[34rem]:w-auto @min-[34rem]:flex-1 @min-[34rem]:basis-0"
          >
            {pictured ? (
              p.photo ? (
                <Photo
                  src={p.photo}
                  alt={p.name}
                  size="thumb"
                  className="h-[140px] w-full shrink-0 overflow-clip rounded-md"
                  imgClassName="object-cover"
                />
              ) : (
                <span className="flex h-[140px] shrink-0 items-center justify-center rounded-md bg-secondary text-xs text-hint">
                  No photo
                </span>
              )
            ) : null}
            <p
              className={cn(
                "text-[15px] leading-[21px] font-semibold text-foreground",
                pictured && "pt-1",
              )}
            >
              {p.name}
            </p>
            {p.brand ? <p className="text-meta leading-[18px] text-hint">{p.brand}</p> : null}
            <p className="font-display text-[22px] leading-[26px] text-moss tabular-nums">
              {p.price}
            </p>
            <p className="text-[14px] leading-5 text-copy">{p.note}</p>
            {p.url ? (
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-fit text-meta leading-[18px] font-medium text-foreground underline decoration-1 underline-offset-2"
              >
                View ↗
              </a>
            ) : null}
          </article>
        ))}
      </div>
      {rows.length ? (
        <>
          {/* Wide: a row per attribute, its label small, its values in the cards' columns. */}
          <dl className="hidden flex-col @min-[34rem]:flex">
            {rows.map((r, i) => (
              <div
                key={i}
                className="flex flex-col gap-[3px] border-b border-border py-[9px] last:border-0"
              >
                <dt className="pl-3 text-xs leading-4 text-hint">{r.label}</dt>
                <dd className="flex gap-3">
                  {r.values.map((v, j) => (
                    <span
                      key={j}
                      className="min-w-0 flex-1 basis-0 pl-3 text-[14px] leading-5 text-copy"
                    >
                      {v}
                    </span>
                  ))}
                </dd>
              </div>
            ))}
          </dl>
          {/* Narrow: a table that scrolls sideways, its label column fixed. */}
          <div className="-mr-4 overflow-x-auto @min-[34rem]:hidden">
            <table className="w-max border-collapse">
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <th
                      scope="row"
                      className="sticky left-0 w-24 min-w-24 bg-background py-[9px] text-left align-top text-meta leading-5 font-normal text-hint shadow-[1px_0_0] shadow-ink/8"
                    >
                      {r.label}
                    </th>
                    {r.values.map((v, j) => (
                      <td
                        key={j}
                        className="w-[130px] min-w-[130px] px-3 py-[9px] align-top text-[14px] leading-5 text-copy"
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Broken (spec §4.4) ──────────────────────────────────────────────────────

function BrokenBlock({
  block,
}: {
  block: TextBlock & { broken: NonNullable<TextBlock["broken"]> };
}) {
  return (
    <div data-block="broken" data-line={block.broken.line} className="flex min-w-0 flex-col gap-2">
      <p className="text-meta leading-[18px] text-brick">
        This {block.broken.type} block couldn't be drawn: {block.broken.why}.
      </p>
      <pre className="overflow-x-auto rounded-md bg-card px-3.5 py-3 shadow-[0_0_0_1px] shadow-ink/8 @min-[34rem]:bg-sheet-tint">
        <code className="font-mono text-xs leading-5 whitespace-pre text-muted-foreground">
          {block.markdown}
        </code>
      </pre>
    </div>
  );
}

// ── The body ────────────────────────────────────────────────────────────────

/** The pieces that draw a block from its fields alone. */
const VIEWS = {
  columns: Columns,
  callout: Callout,
  figure: Figure,
  facts: Facts,
  details: Details,
  rows: Rows,
  steps: Steps,
  product: Product,
  products: Products,
  chart: Chart,
} as const;

function BlockView({ block, textClassName }: { block: Block; textClassName?: string }) {
  if (block.type === "text") {
    return block.broken ? (
      <BrokenBlock block={{ ...block, broken: block.broken }} />
    ) : (
      <Text markdown={block.markdown} className={textClassName} />
    );
  }
  if (block.type === "section") return <Section block={block} />;
  if (block.type === "closing") return <Closing text={block.text} />;
  const View = VIEWS[block.type] as (props: { block: typeof block }) => ReactNode;
  return <View block={block} />;
}

function Section({ block }: { block: SectionBlock }) {
  return (
    <section
      data-block="section"
      className="flex flex-col gap-3 border-t border-border pt-[18px] @min-[34rem]:gap-3.5 @min-[34rem]:pt-[22px]"
    >
      <SectionLabel>{block.label}</SectionLabel>
      {block.blocks.map((b, i) => (
        <BlockView key={i} block={b} />
      ))}
    </section>
  );
}

function Blocks({
  blocks,
  className,
  textClassName,
}: {
  blocks: readonly Block[];
  className?: string;
  textClassName?: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col", className)}>
      {blocks.map((b, i) => (
        <BlockView key={i} block={b} textClassName={textClassName} />
      ))}
    </div>
  );
}

/** A doc's body, read: every block of the kit drawn, a broken one as its source. */
export function DocBlocks({ body, className }: { body: string; className?: string }) {
  const blocks = useMemo(() => parseBlocks(body), [body]);
  return (
    <div data-doc-blocks className={cn("@container", className)}>
      <Blocks blocks={blocks} className="gap-[18px] @min-[34rem]:gap-5" />
    </div>
  );
}
